import { apiClient } from './api-client';

export enum FileCategory {
  ATTACHMENT = 'attachment',
  AVATAR = 'avatar',
  DOCUMENT = 'document',
  IMAGE = 'image',
  OTHER = 'other',
}

export interface FileUploadMetadata {
  category?: FileCategory;
  entityType?: string;
  entityId?: string;
  isPublic?: boolean;
}

export interface FileResponse {
  id: string;
  tenantId?: string;
  filename: string;
  originalFilename: string;
  mimeType: string;
  size: number;
  url?: string;
  storageUrl?: string;
  storagePath?: string;
  storageProvider?: string;
  status?: string;
  category?: FileCategory;
  entityType?: string;
  entityId?: string;
  isPublic?: boolean;
  uploadedBy: string;
  createdAt: string;
  updatedAt?: string;
}

/** One file that failed to upload, with a human-readable reason. */
export interface FailedUpload {
  file: File;
  error: string;
}

/** Per-file outcome of a multi-file upload (see uploadFilesSettled). */
export interface UploadFilesResult {
  succeeded: FileResponse[];
  failed: FailedUpload[];
}

export interface FileFilter {
  category?: FileCategory;
  entityType?: string;
  entityId?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedFilesResponse {
  data: FileResponse[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PresignedUrlResponse {
  url: string;
  expiresAt: string;
}

/** fileId → blob: URL of its bytes; see FileService.getFileObjectUrl. */
const fileObjectUrls = new Map<string, Promise<string>>();

class FileService {
  /**
   * Upload a file with optional metadata
   */
  async uploadFile(
    file: File,
    metadata: FileUploadMetadata = {}
  ): Promise<FileResponse> {
    const formData = new FormData();
    formData.append('file', file);

    if (metadata.category) {
      formData.append('category', metadata.category);
    }
    if (metadata.entityType) {
      formData.append('entityType', metadata.entityType);
    }
    if (metadata.entityId) {
      formData.append('entityId', metadata.entityId);
    }
    if (metadata.isPublic !== undefined) {
      formData.append('isPublic', String(metadata.isPublic));
    }

    const response = await apiClient.post<FileResponse>('/files/upload', formData);
    return response.data;
  }

  /**
   * Upload multiple files.
   *
   * NOTE: uses Promise.all, so it REJECTS on the first failed file and does not
   * surface which files succeeded. For multi-file uploads where a partial
   * failure should be reported per file, prefer {@link uploadFilesSettled}.
   */
  async uploadFiles(
    files: File[],
    metadata: FileUploadMetadata = {}
  ): Promise<FileResponse[]> {
    const uploadPromises = files.map(file => this.uploadFile(file, metadata));
    return Promise.all(uploadPromises);
  }

  /**
   * Upload multiple files independently, reporting per-file outcomes instead of
   * failing the whole batch on the first error. Each file is its own request, so
   * some can succeed while others fail (e.g. one file over the server limit).
   * Never rejects — inspect `failed` for the per-file reasons.
   */
  async uploadFilesSettled(
    files: File[],
    metadata: FileUploadMetadata = {}
  ): Promise<UploadFilesResult> {
    const settled = await Promise.allSettled(
      files.map((file) => this.uploadFile(file, metadata)),
    );
    const succeeded: FileResponse[] = [];
    const failed: FailedUpload[] = [];
    settled.forEach((res, i) => {
      if (res.status === 'fulfilled') {
        succeeded.push(res.value);
      } else {
        failed.push({ file: files[i], error: this.extractUploadError(res.reason) });
      }
    });
    return { succeeded, failed };
  }

  /** Turn an upload error into a short, human-readable reason for the toast. */
  private extractUploadError(err: any): string {
    if (err?.response?.status === 413) return 'File too large';
    return (
      err?.response?.data?.message ||
      err?.response?.data?.error?.message ||
      err?.message ||
      'Upload failed'
    );
  }

  /**
   * Get files with optional filters
   */
  async getFiles(filter: FileFilter = {}): Promise<PaginatedFilesResponse> {
    const params = new URLSearchParams();
    if (filter.category) params.append('category', filter.category);
    if (filter.entityType) params.append('entityType', filter.entityType);
    if (filter.entityId) params.append('entityId', filter.entityId);
    if (filter.page) params.append('page', String(filter.page));
    if (filter.limit) params.append('limit', String(filter.limit));

    const response = await apiClient.get<PaginatedFilesResponse>(`/files?${params.toString()}`);
    return response.data;
  }

  /**
   * Get file by ID
   */
  async getFileById(id: string): Promise<FileResponse> {
    const response = await apiClient.get<FileResponse>(`/files/${id}`);
    return response.data;
  }

  /**
   * Get files by entity (e.g., all files attached to a ticket)
   */
  async getFilesByEntity(entityType: string, entityId: string): Promise<FileResponse[]> {
    const response = await apiClient.get<FileResponse[]>(`/files/entity/${entityType}/${entityId}`);
    return response.data;
  }

  /**
   * Delete a file
   */
  async deleteFile(id: string): Promise<void> {
    await apiClient.delete(`/files/${id}`);
  }

  /**
   * Get presigned URL for file download
   */
  async getPresignedUrl(id: string, expiresIn?: number): Promise<PresignedUrlResponse> {
    const response = await apiClient.post<PresignedUrlResponse>(`/files/${id}/presigned-url`, {
      expiresIn,
    });
    return response.data;
  }

  /**
   * Get download URL for file (BFF streams the file at this path)
   */
  getDownloadUrl(id: string): string {
    return `/api/files/${id}/download`;
  }

  /**
   * Download a file directly
   */
  downloadFile(id: string): void {
    window.open(this.getDownloadUrl(id), '_blank');
  }

  /**
   * A `blob:` URL holding a stored file's bytes, fetched through the
   * authenticated API client. For `<img>` tags that need a file (e.g. inline
   * email images): a plain `src="/api/files/<id>/download"` sends no Bearer
   * token and 401s. Stored files never change, so each id is fetched once per
   * page load and shared by every caller; a failed fetch is not cached.
   */
  getFileObjectUrl(id: string): Promise<string> {
    let pending = fileObjectUrls.get(id);
    if (!pending) {
      pending = apiClient
        .get(`/files/${id}/download`, { responseType: 'blob' })
        .then((response) => URL.createObjectURL(response.data as Blob));
      pending.catch(() => fileObjectUrls.delete(id));
      fileObjectUrls.set(id, pending);
    }
    return pending;
  }

  /**
   * Download an attachment through the authenticated API client.
   *
   * A plain `<a href>` link can't send the `Authorization: Bearer` token the
   * download endpoint requires (→ 401). So for same-origin API paths
   * (`/api/files/<id>/download`) we fetch the bytes via the apiClient (which
   * adds the token), then save them through a temporary blob URL. Absolute
   * URLs (e.g. signed Azure SAS / public links) are opened directly.
   */
  async downloadAttachment(attachment: { url: string; name: string }): Promise<void> {
    const { url, name } = attachment;
    if (!url) return;

    // Same-origin API path → fetch with auth, then save as a blob.
    if (url.startsWith('/api/')) {
      const path = url.replace(/^\/api/, ''); // strip the apiClient baseURL ("/api")
      const response = await apiClient.get(path, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = name || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
      return;
    }

    // Absolute/external URL (SAS, public, …) → open directly.
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file icon based on MIME type
   */
  getFileIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.startsWith('video/')) return '🎬';
    if (mimeType.startsWith('audio/')) return '🎵';
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return '📊';
    if (mimeType.includes('document') || mimeType.includes('word')) return '📝';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return '📑';
    if (mimeType.includes('zip') || mimeType.includes('compressed')) return '🗜️';
    return '📎';
  }

  /**
   * Check if file is an image
   */
  isImage(mimeType: string): boolean {
    return mimeType.startsWith('image/');
  }

  /**
   * Check if file is previewable
   */
  isPreviewable(mimeType: string): boolean {
    return (
      mimeType.startsWith('image/') ||
      mimeType === 'application/pdf' ||
      mimeType.startsWith('text/')
    );
  }
}

export const fileService = new FileService();
export default fileService;
